// CBuDInfo.tsx
import React from "react";
import { Box, Typography, IconButton, Tooltip, Popover } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface CBuDInfoProps {
    documentId?: string;
    iconSize?: "small" | "medium" | "large";
    iconColor?: string;
}

const CBuDInfoContent: React.FC = () => {
    return (
        <Box sx={{ p: 2, maxWidth: 420 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1.5 }}>
                CBuD Report – How to Proceed
            </Typography>

            <Typography variant="body2" sx={{ mb: 1.5, fontSize: "0.875rem" }}>
                The CBuD (Call Before u Dig) Report is mandatory at present as per
                MoRTH directions for applicable applications involving digging/excavation
                activities. Before uploading this document on Rajmarg Pravesh, please
                complete the following:
            </Typography>

            <Box
                component="ol"
                sx={{
                    pl: 2,
                    mb: 1.5,
                    "& li": {
                        fontSize: "0.875rem",
                        mb: 0.75,
                        lineHeight: 1.5,
                    },
                }}
            >
                <li>Open the CBuD (Call Before u Dig) App/Portal.</li>
                <li>
                    Submit a prior digging notice for the proposed work. Enter the required
                    details of the work, including the proposed location/route and other
                    information requested in the CBuD application.
                </li>
                <li>Submit the notice on CBuD.</li>
                <li>
                    Download the CBuD Report generated after successful submission of the
                    notice.
                </li>
                <li>
                    Return to Rajmarg Pravesh Portal and upload the downloaded CBuD Report
                    in this field.
                </li>
            </Box>

            <Typography
                variant="body2"
                sx={{
                    fontWeight: "bold",
                    color: "#d32f2f",
                    mb: 0.75,
                    fontSize: "0.875rem",
                }}
            >
                Important:
            </Typography>

            <Box
                component="ul"
                sx={{
                    pl: 2,
                    "& li": {
                        fontSize: "0.875rem",
                        mb: 0.75,
                        lineHeight: 1.5,
                    },
                }}
            >
                <li>
                    Upload the report generated/downloaded from the CBuD App/Portal, not a
                    screenshot of the CBuD application.
                </li>
                <li>
                    Ensure that the CBuD Report relates to the same proposed work/location
                    mentioned in your Rajmarg Pravesh application.
                </li>
                <li>
                    The CBuD Report must be uploaded before you can proceed with the
                    applicable application stage.
                </li>
            </Box>
        </Box>
    );
};

const CBuDInfo: React.FC<CBuDInfoProps> = ({
    documentId = "cbud-info",
    iconSize = "small",
    iconColor =  "#3A1C73"
}) => {
    const [hoverAnchorEl, setHoverAnchorEl] = React.useState<HTMLElement | null>(
        null
    );
    const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<HTMLElement | null>(
        null
    );

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setPopoverAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setPopoverAnchorEl(null);
    };

    // Fix: Use React.SyntheticEvent<Element> for Tooltip handlers
    const handleHoverOpen = (event: React.SyntheticEvent<Element>) => {
        setHoverAnchorEl(event.currentTarget as HTMLElement);
    };

    const handleHoverClose = () => {
        setHoverAnchorEl(null);
    };

    const isPopoverOpen = Boolean(popoverAnchorEl);
    const isHoverOpen = Boolean(hoverAnchorEl);

    return (
        <>
            {/* Hover Tooltip */}
            <Tooltip
                title={<CBuDInfoContent />}
                arrow
                placement="top"
                open={isHoverOpen}
                onOpen={handleHoverOpen}
                onClose={handleHoverClose}
                componentsProps={{
                    tooltip: {
                        sx: {
                            maxWidth: 450,
                            bgcolor: "#ffffff",
                            color: "#333333",
                            boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
                            border: "1px solid #e0e0e0",
                            p: 0,
                            "& .MuiTooltip-arrow": {
                                color: "#ffffff",
                            },
                        },
                    },
                }}
            >
                <IconButton
                    id={documentId}
                    size={iconSize}
                    sx={{
                        color: iconColor,
                        ml: 0.5,
                        "&:hover": {
                            backgroundColor: "rgba(25, 118, 210, 0.04)",
                        },
                    }}
                    onClick={handlePopoverOpen}
                    onMouseEnter={handleHoverOpen}
                    onMouseLeave={handleHoverClose}
                >
                    <InfoOutlinedIcon fontSize={iconSize} />
                </IconButton>
            </Tooltip>

            {/* Click Popover - for mobile and accessibility */}
            <Popover
                open={isPopoverOpen}
                anchorEl={popoverAnchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                PaperProps={{
                    sx: {
                        maxWidth: 450,
                        maxHeight: 500,
                        overflow: "auto",
                        borderRadius: 2,
                        boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
                    },
                }}
            >
                <CBuDInfoContent />
            </Popover>
        </>
    );
};

export default CBuDInfo;